import React from "react";
import { Link } from "react-router-dom";
import { Container, Row, Col, Card, CardBody } from "reactstrap";

const TermsOfService = () => {
  document.title = "Terms of Service | Porcher";

  return (
    <div className="page-content">
      <Container>
        <Row className="justify-content-center">
          <Col lg={8}>
            <Card className="border-0 shadow-sm mt-4">
              <CardBody className="p-4">
                <h1 className="mb-3">Terms of Service</h1>
                <p className="text-muted small mb-4">Last updated: August 22, 2026</p>

                <div className="content">
                  <h5 className="mt-4">1. Acceptance of Terms</h5>
                  <p>
                    These Terms of Service ("Terms") are a binding agreement between you and Patronage Reservoir LLC,
                    a Nevada limited liability company doing business as Porcher ("Porcher", "we", "our", or "us"),
                    governing your access to and use of the Porcher website, mobile experience, and related services
                    (collectively, the "Platform"). By creating an account or otherwise using the Platform, you agree
                    to these Terms. If you do not agree, do not use the Platform.
                  </p>

                  <h5 className="mt-4">2. Eligibility &amp; Accounts</h5>
                  <p>
                    You must be at least 18 years old and able to form a legally binding contract to use the Platform.
                    You are responsible for the accuracy of the information you provide, for keeping your login
                    credentials confidential, and for all activity that occurs under your account. Notify us
                    immediately through the support portal if you suspect unauthorized use of your account.
                  </p>

                  <h5 className="mt-4">3. What Porcher Is — and Is Not</h5>
                  <p>
                    Porcher is a self-service software platform that helps independent property owners ("Homeowners"
                    or "PMs") list rental properties, screen applicants, execute leases, collect rent, and communicate
                    with tenants. Porcher is <strong>not</strong> a licensed property management company, real estate
                    broker, insurer, consumer reporting agency, law firm, or debt collector, and does not provide
                    legal, tax, or financial advice. Porcher is not a party to any lease, rental application, or
                    payment arrangement between a Homeowner and a Tenant/Applicant — those relationships and their
                    legal obligations remain solely between the Homeowner and the Tenant/Applicant.
                  </p>

                  <h5 className="mt-4">4. Homeowner &amp; Tenant Responsibilities</h5>
                  <p>
                    Homeowners are solely responsible for: complying with all applicable landlord-tenant, fair
                    housing, consumer reporting, and payment laws; the accuracy of their listings; the tenant
                    selection decisions they make; and the terms of any lease they sign. Tenants and Applicants are
                    responsible for the accuracy of the information they submit and for complying with the terms of
                    any lease they enter into. See our{" "}
                    <Link to="/tenant-screening-policy">Tenant Screening &amp; FCRA Policy</Link> and{" "}
                    <Link to="/fair-housing-policy">Fair Housing &amp; Anti-Discrimination Policy</Link> for more on
                    screening and tenant-selection obligations.
                  </p>

                  <h5 className="mt-4">5. Acceptable Use &amp; Community Standards</h5>
                  <p>
                    Use of messaging, uploads, and other interactive features on the Platform is governed by our{" "}
                    <Link to="/community-guidelines">Community &amp; Acceptable Use Policy</Link>, which is
                    incorporated into these Terms by reference. Without limiting that policy, you agree not to:
                  </p>
                  <ul>
                    <li>Use the Platform for any unlawful, fraudulent, or discriminatory purpose</li>
                    <li>Harass, threaten, or abuse other users, or transmit hateful or discriminatory content</li>
                    <li>Upload malicious code, or content you don't have the right to share</li>
                    <li>Attempt to gain unauthorized access to any account, data, or system on the Platform</li>
                    <li>Reverse engineer, scrape, or resell the Platform without our written permission</li>
                  </ul>

                  <h5 className="mt-4">6. User Content &amp; Communications</h5>
                  <p>
                    You retain ownership of the listings, photos, documents, and messages you submit ("User Content"),
                    and grant Porcher a limited license to host, display, and transmit that content solely to operate
                    and improve the Platform. In-app messages (property chat, inspection chat, and ticket
                    conversations) are stored so the relevant participants have a record of the conversation and so
                    Porcher can investigate disputes or misuse — see our{" "}
                    <Link to="/privacy-policy">Privacy Policy</Link> for details on who can access this data.
                  </p>

                  <h5 className="mt-4">7. Payments &amp; Fees</h5>
                  <p>
                    Application fees, rent payments, and any other charges made through the Platform are processed by
                    Stripe and governed by our <Link to="/payments-refund-policy">Payments &amp; Refund Policy</Link>,
                    which is incorporated into these Terms by reference.
                  </p>

                  <h5 className="mt-4">8. Tenant Screening</h5>
                  <p>
                    Background, credit, criminal, and eviction screening is performed by an independent third-party
                    consumer reporting agency, not by Porcher. Screening is subject to our{" "}
                    <Link to="/tenant-screening-policy">Tenant Screening &amp; FCRA Policy</Link>. Porcher does not
                    guarantee the accuracy, completeness, or outcome of any screening report, and does not make or
                    influence any Homeowner's tenant-selection decision.
                  </p>

                  <h5 className="mt-4">9. Third-Party Services</h5>
                  <p>
                    The Platform relies on third-party providers — including Stripe (payments), our background-check
                    vendor (screening reports), and cloud infrastructure providers (hosting, storage, and
                    authentication) — to operate. We are not responsible for outages, errors, or losses caused by a
                    third-party provider acting outside our control, though we will work in good faith to resolve
                    issues that affect you.
                  </p>

                  <h5 className="mt-4">10. Intellectual Property</h5>
                  <p>
                    The Platform, including its design, software, and branding, is owned by Porcher and protected by
                    intellectual property laws. We grant you a limited, non-exclusive, non-transferable, revocable
                    license to use the Platform for its intended purpose. You may not copy, modify, or distribute any
                    part of the Platform except as expressly permitted.
                  </p>

                  <h5 className="mt-4">11. Disclaimers</h5>
                  <p>
                    THE PLATFORM AND ALL MATERIALS ON IT ARE PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES OF
                    ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
                    PURPOSE, TITLE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED,
                    ERROR-FREE, OR SECURE, OR THAT ANY SCREENING REPORT, LISTING, OR APPLICATION WILL BE ACCURATE OR
                    THAT ANY TENANT OR APPLICANT WILL PERFORM AS EXPECTED. PORCHER DOES NOT GUARANTEE A "SAFE," "BEST,"
                    OR RISK-FREE TENANT OUTCOME — DECISIONS BASED ON INFORMATION PROVIDED THROUGH THE PLATFORM ARE MADE
                    AT THE HOMEOWNER'S OWN DISCRETION AND RISK.
                  </p>

                  <h5 className="mt-4">12. Limitation of Liability</h5>
                  <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, PORCHER AND ITS OFFICERS, EMPLOYEES, AND SERVICE PROVIDERS
                    WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
                    INCLUDING LOST RENT, LOST PROFITS, OR DAMAGES ARISING FROM A TENANT'S OR APPLICANT'S CONDUCT,
                    ARISING OUT OF OR RELATING TO YOUR USE OF THE PLATFORM. OUR TOTAL LIABILITY FOR ANY CLAIM RELATING
                    TO THE PLATFORM WILL NOT EXCEED THE FEES YOU PAID TO PORCHER IN THE 12 MONTHS BEFORE THE CLAIM
                    AROSE.
                  </p>

                  <h5 className="mt-4">13. Indemnification</h5>
                  <p>
                    You agree to indemnify and hold Porcher harmless from any claims, damages, or expenses (including
                    reasonable attorneys' fees) arising from your use of the Platform, your violation of these Terms,
                    your violation of any law (including fair housing or consumer reporting law), or your lease or
                    tenancy relationship with another user.
                  </p>

                  <h5 className="mt-4">14. Service Availability &amp; Incidents</h5>
                  <p>
                    We aim to keep the Platform available and secure, but we do not guarantee uninterrupted access.
                    We may suspend the Platform for maintenance, upgrades, or events outside our reasonable control
                    (including third-party outages). If we become aware of a security incident that affects your
                    account data, we will notify affected users as required by applicable law and take reasonable
                    steps to investigate and remediate the incident.
                  </p>

                  <h5 className="mt-4">15. Suspension &amp; Termination</h5>
                  <p>
                    We may suspend or terminate your access to the Platform, with or without notice, if we reasonably
                    believe you have violated these Terms, our{" "}
                    <Link to="/community-guidelines">Community &amp; Acceptable Use Policy</Link>, or applicable law,
                    or to protect the safety or rights of other users. You may stop using the Platform and close your
                    account at any time by contacting support.
                  </p>

                  <h5 className="mt-4">16. Dispute Resolution &amp; Governing Law</h5>
                  <p>
                    These Terms are governed by the laws of Nevada, without regard to conflict-of-law
                    principles. Before filing a claim, you agree to contact us through the support portal so we can
                    attempt to resolve the dispute informally. Any dispute not resolved informally will be subject to
                    the exclusive jurisdiction of the courts located in Nevada, unless applicable law
                    requires otherwise.
                  </p>

                  <h5 className="mt-4">17. Changes to These Terms</h5>
                  <p>
                    We may update these Terms from time to time. If we make material changes, we will provide
                    reasonable notice (such as an in-app notice or updated "Last updated" date). Continued use of the
                    Platform after changes take effect means you accept the revised Terms.
                  </p>

                  <h5 className="mt-4">18. Severability &amp; Entire Agreement</h5>
                  <p>
                    If any provision of these Terms is found unenforceable, the remaining provisions remain in full
                    effect. These Terms, together with the policies linked throughout this document, are the entire
                    agreement between you and Porcher regarding the Platform.
                  </p>

                  <h5 className="mt-4">19. Contact Us</h5>
                  <p>
                    If you have any questions about these Terms of Service, please contact us through the support
                    portal.
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default TermsOfService;
